import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import useDeduplicatedSubscription from '/imports/ui/core/hooks/useDeduplicatedSubscription';
import CURRENT_USER_SUBSCRIPTION from '/imports/ui/core/graphql/queries/currentUserSubscription';
import { SET_PRESENTER } from '/imports/ui/core/graphql/mutations/userMutations';
import logger from '/imports/startup/client/logger';
import Auth from '/imports/ui/services/auth';

/**
 * AutoPresenterHandler - Automatically assigns presenter role to moderators
 *
 * In BBB, moderators can manage the meeting but only presenters can share screens.
 * This component ensures tutors (moderators) automatically become presenters when
 * they join the meeting, enabling them to share their screen immediately.
 */
const AutoPresenterHandler: React.FC = () => {
  const { data: currentUserData } = useDeduplicatedSubscription(CURRENT_USER_SUBSCRIPTION);
  const [setPresenter] = useMutation(SET_PRESENTER);
  const [hasAttemptedPresenterAssignment, setHasAttemptedPresenterAssignment] = useState(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const attemptsRef = useRef(0);
  const lastUserIdRef = useRef<string | null>(null);
  const userRef = useRef<any>(null);

  const clearRetryTimeout = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const user = currentUserData?.user_current?.[0];
    userRef.current = user ?? null;

    // Wait until user data is loaded
    if (!user) return;

    // Reset attempts when user changes
    if (user.userId !== lastUserIdRef.current) {
      lastUserIdRef.current = user.userId;
      attemptsRef.current = 0;
      clearRetryTimeout();
      setHasAttemptedPresenterAssignment(false);
    }

    // Only proceed for moderators who have joined
    if (!user.isModerator || !user.joined) return;

    // Check if already presenter
    if (user.presenter) {
      logger.info({
        logCode: 'auto_presenter_already_presenter',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
        },
      }, `Moderator ${user.name} is already presenter, skipping auto-assignment`);
      setHasAttemptedPresenterAssignment(true);
      attemptsRef.current = 0;
      clearRetryTimeout();
      return;
    }

    const MAX_ATTEMPTS = 5;
    const RETRY_DELAY_MS = 1500;

    if (attemptsRef.current >= MAX_ATTEMPTS) {
      logger.warn({
        logCode: 'auto_presenter_max_attempts_reached',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          attempts: attemptsRef.current,
        },
      }, `Auto-presenter max attempts reached for ${user.name}`);
      setHasAttemptedPresenterAssignment(true);
      return;
    }

    // Assign presenter role to this moderator
    logger.info({
      logCode: 'auto_presenter_assigning',
      extraInfo: {
        userId: user.userId,
        userName: user.name,
        attempt: attemptsRef.current + 1,
      },
    }, `Auto-assigning presenter role to moderator: ${user.name}`);

    setHasAttemptedPresenterAssignment(true);

    // Small delay to ensure the meeting is fully initialized
    clearRetryTimeout();
    retryTimeoutRef.current = setTimeout(() => {
      attemptsRef.current += 1;
      setPresenter({ variables: { userId: Auth.userID } })
        .then(() => {
          logger.info({
            logCode: 'auto_presenter_assigned_success',
            extraInfo: {
              userId: user.userId,
              userName: user.name,
              attempt: attemptsRef.current,
            },
          }, `Successfully assigned presenter role to moderator: ${user.name}`);

          // Verify presenter status; retry if still not presenter after a short delay
          setTimeout(() => {
            const latestUser = userRef.current;
            if (latestUser && !latestUser.presenter && attemptsRef.current < MAX_ATTEMPTS) {
              clearRetryTimeout();
              retryTimeoutRef.current = setTimeout(() => {
                const retryUser = userRef.current;
                if (!retryUser || !retryUser.isModerator || !retryUser.joined) return;
                if (retryUser.presenter) return;
                logger.info({
                  logCode: 'auto_presenter_retrying',
                  extraInfo: {
                    userId: retryUser.userId,
                    userName: retryUser.name,
                    attempt: attemptsRef.current + 1,
                  },
                }, `Retrying presenter assignment for ${retryUser.name}`);
                attemptsRef.current += 1;
                setPresenter({ variables: { userId: Auth.userID } })
                  .catch((error) => {
                    logger.error({
                      logCode: 'auto_presenter_retry_failed',
                      extraInfo: {
                        userId: retryUser.userId,
                        userName: retryUser.name,
                        errorName: error.name,
                        errorMessage: error.message,
                        attempt: attemptsRef.current,
                      },
                    }, `Presenter assignment retry failed: ${error.message}`);
                  });
              }, RETRY_DELAY_MS);
            }
          }, 1200);
        })
        .catch((error) => {
          logger.error({
            logCode: 'auto_presenter_assigned_failed',
            extraInfo: {
              userId: user.userId,
              userName: user.name,
              errorName: error.name,
              errorMessage: error.message,
              attempt: attemptsRef.current,
            },
          }, `Failed to assign presenter role to moderator: ${error.message}`);

          // Retry after delay if still not presenter
          if (attemptsRef.current < MAX_ATTEMPTS) {
            clearRetryTimeout();
            retryTimeoutRef.current = setTimeout(() => {
              const retryUser = userRef.current;
              if (!retryUser || !retryUser.isModerator || !retryUser.joined) return;
              if (retryUser.presenter) return;
              logger.info({
                logCode: 'auto_presenter_retrying_after_failure',
                extraInfo: {
                  userId: retryUser.userId,
                  userName: retryUser.name,
                  attempt: attemptsRef.current + 1,
                },
              }, `Retrying presenter assignment after failure for ${retryUser.name}`);
              attemptsRef.current += 1;
              setPresenter({ variables: { userId: Auth.userID } })
                .catch((retryError) => {
                  logger.error({
                    logCode: 'auto_presenter_retry_failed',
                    extraInfo: {
                      userId: retryUser.userId,
                      userName: retryUser.name,
                      errorName: retryError.name,
                      errorMessage: retryError.message,
                      attempt: attemptsRef.current,
                    },
                  }, `Presenter assignment retry failed: ${retryError.message}`);
                });
            }, RETRY_DELAY_MS);
          }
        });
    }, 1000);
  }, [currentUserData, hasAttemptedPresenterAssignment, setPresenter]);

  useEffect(() => {
    return () => {
      clearRetryTimeout();
    };
  }, []);

  return null;
};

export default AutoPresenterHandler;
